import React, { useEffect, useState } from "react";
import { GraduationCap, CheckCircle2, XCircle, Download, Clock } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";

export default function CourseQuiz({ courseId, certificateId, onPassed }) {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [passedNow, setPassedNow] = useState(!!certificateId);
  const [certId, setCertId] = useState(certificateId);

  const load = () => {
    setErr("");
    api
      .get(`/my/courses/${courseId}/quiz`)
      .then((r) => setQuiz(r.data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Sınav yüklenemedi."));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseId]);

  const selectAnswer = (qIndex, optionIndex) => {
    setAnswers((a) => ({ ...a, [qIndex]: optionIndex }));
  };

  const submit = async () => {
    if (!quiz?.questions?.length) return;
    const answerList = quiz.questions.map((_, i) => (answers[i] ?? -1));
    setSubmitting(true);
    setErr("");
    try {
      const { data } = await api.post(`/my/courses/${courseId}/quiz/submit`, { answers: answerList });
      setResult(data);
      if (data.passed) {
        setPassedNow(true);
        setCertId(data.certificate_id);
        onPassed?.(data.certificate_id);
      } else {
        load();
        setAnswers({});
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Sınav gönderilemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  if (passedNow || quiz?.passed) {
    return (
      <div className="bg-surface border border-gold-30 p-8 text-center">
        <CheckCircle2 className="w-10 h-10 text-gold mx-auto mb-3" />
        <div className="font-display text-2xl mb-2">Sınavı geçtiniz, tebrikler!</div>
        <p className="text-white/60 text-sm mb-5">Sertifikanız hazır.</p>
        {(certId || certificateId) && (
          <a
            href={`${API}/certificates/${certId || certificateId}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="btn-primary text-sm inline-flex"
          >
            <Download className="w-4 h-4" /> Sertifikanı İndir
          </a>
        )}
      </div>
    );
  }

  if (err) {
    return (
      <div className="bg-red-950/40 border border-red-800 text-red-200 p-4 text-sm">{err}</div>
    );
  }

  if (!quiz) {
    return <div className="text-white/50 text-sm">Sınav yükleniyor...</div>;
  }

  if (quiz.locked) {
    return null;
  }

  if (quiz.cooldown) {
    return (
      <div className="bg-surface border border-white/10 p-8 text-center">
        <Clock className="w-8 h-8 text-gold mx-auto mb-3" />
        <div className="font-display text-xl mb-2">Deneme hakkınız doldu</div>
        <p className="text-white/60 text-sm">
          {quiz.cooldown.remaining_minutes} dakika sonra tekrar deneyebilirsiniz.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-gold-30 p-6 md:p-8">
      <div className="flex items-center gap-3 mb-2">
        <GraduationCap className="w-6 h-6 text-gold" />
        <h2 className="font-display text-2xl">Sertifika Sınavı</h2>
      </div>
      <p className="text-white/50 text-sm mb-6">
        Geçmek için %{quiz.pass_percent} başarı gerekiyor. Hak: {quiz.max_attempts - quiz.attempts_used} / {quiz.max_attempts}
      </p>

      {result && !result.passed && (
        <div className="bg-red-950/40 border border-red-800 text-red-200 p-4 text-sm mb-6 flex items-center gap-2">
          <XCircle className="w-4 h-4 flex-shrink-0" />
          Skorunuz %{result.score}. Geçmek için %{result.pass_percent} gerekiyor. Tekrar deneyebilirsiniz.
        </div>
      )}

      <div className="space-y-6">
        {quiz.questions.map((q, qi) => (
          <div key={qi} className="border-b border-white/10 pb-5 last:border-0">
            <div className="text-sm font-medium mb-3">
              {qi + 1}. {q.question}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {q.options.map((opt, oi) => (
                <button
                  key={oi}
                  onClick={() => selectAnswer(qi, oi)}
                  className={`text-left text-sm px-4 py-2.5 border transition-colors ${
                    answers[qi] === oi
                      ? "border-gold bg-gold/10 text-gold"
                      : "border-white/10 hover:border-white/30 text-white/70"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={submit}
        disabled={submitting || Object.keys(answers).length < quiz.questions.length}
        className="btn-primary text-sm mt-6 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitting ? "Gönderiliyor..." : "Sınavı Tamamla"}
      </button>
    </div>
  );
}
